/* AppraisalComponents/hooks/flashingRateResolver.js
 * Iter316 · P0-1 · Flashing cascade wire-up (2026-02c).
 *
 * Pure helper — given (material, girth_mm, bends, rates.Flashing) returns
 * the smallest catalogue rate row whose width ≥ girth AND folds === bends
 * AND type_name contains the material token. Otherwise returns null.
 *
 * Extracted from ComponentLineRow.js `resolveRateByGirthFolds` (Iter248)
 * so both the row-level Valley/Barge cascade AND the new parent-level
 * `useFlashingRateCascade` hook can share the same matcher. Zero
 * behavioural change vs the row-local version — same regex, same
 * material-token check, same "next catalogue width ≥ girth" rounding.
 *
 * Naming convention on flashing rate rows:
 *   "<Material> <Width>W/<Folds>F"  e.g. "Galv 400W/2F", "Alu 100W/8F"
 * Valley rows use a "(400G/5F)" pattern — kept as the first regex
 * (backward-compat with Iter248's caller behaviour).
 */

export function resolveRateByGirthFolds(material, girth, bends, rates) {
  if (!material || !girth || girth <= 0 || !bends || bends <= 0) return null;
  const mat = String(material).toLowerCase();
  const cands = (rates || [])
    .map((r) => {
      const tn = String(r.type_name || '').toLowerCase();
      if (!tn.includes(mat) || !tn.includes(`${bends}f`)) return null;
      // Valley pattern "(400G/5F)" first, Flashing pattern "100W/4F" as fallback.
      let m = tn.match(/(\d+)g\//);
      if (!m) m = tn.match(/(\d+)w[/\s)]/);
      return m ? { row: r, width: parseInt(m[1], 10) } : null;
    })
    .filter(Boolean)
    .filter((c) => c.width >= girth)
    .sort((a, b) => a.width - b.width);
  return cands.length ? cands[0].row : null;
}

// Iter316 · P0-1 · Derived folds_qty helper.
// Operator's mental model: "folds" = bends BETWEEN segments. So a
// 3-segment fold array [20, 330, 20] has 2 bends → folds_qty=2 → SKU
// says `2F`. When a component_line carries `folds` (array of segments)
// but no explicit `folds_qty`, derive it as max(0, len(folds) - 1).
export function deriveFoldsQty(line) {
  const explicit = Number(line?.folds_qty);
  if (Number.isFinite(explicit) && explicit > 0) return explicit;
  const folds = Array.isArray(line?.folds) ? line.folds : null;
  if (folds && folds.length > 1) return folds.length - 1;
  return 0;
}
